import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useCallback } from 'react';
import { useIntl } from 'react-intl';
import {
	ChatAttachmentView,
	ChatDialogEvent,
	ChatDialogEventDirection,
	ChatDialogEventType,
	spacesCorrection,
	tagsCleaning,
} from 'sdk';

import { useSDK } from '../../../../../app';
import { useUIState } from '../../../../../app/App';
import { commonMessages } from '../../../../../i18n/commonMessages';
import { styled } from '../../../../../styled';
import { Theme } from '../../../../../theme/interface/detailed';
import { useSanitize } from '../../../../../utils/useSanitize';
import { ReplyAttachmentPreview } from './ReplyAttachmentPreview';

const styles = css`
	display: grid;
	grid-template-columns: max-content;
	cursor: pointer;
	flex: 1;
	& > span {
		font-weight: 700;
		grid-column: 2 / 3;
	}
	& > div {
		overflow-wrap: normal;
		word-break: break-word;
		text-overflow: ellipsis;
		overflow: hidden;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		grid-area: 2 / 2 / 3 / 3;
	}
	& > span:last-child,
	img:last-child {
		object-fit: cover;
		grid-area: 1 / 1 / 3 / 2;
		width: 36px;
		height: 36px;
		margin-right: 5px;
		padding: 0;
		display: flex;
	}
`;
type Interface = { className: string; message: ChatDialogEvent };

export const Component = ({ className, message }: Interface): ReactElement => {
	const { accountsStore } = useSDK();
	const { scrollStore } = useUIState();
	const { formatMessage } = useIntl();
	const { accountId, view, id, conversationId } = message;
	const text = useSanitize(view.text ? spacesCorrection(tagsCleaning(view.text)) : '');
	const agentInfo = accountId && accountsStore.getAccountInfo(accountId);
	const attachId = (view as ChatAttachmentView).attachId;

	const onClickHandler = useCallback((): void => {
		scrollStore.scrollToElm(id, conversationId);
	}, [scrollStore, id, conversationId]);
	return (
		<div className={className} onClick={onClickHandler}>
			<span>
				{message.direction === ChatDialogEventDirection.FromVisitor
					? formatMessage(commonMessages.replyYou)
					: (agentInfo && agentInfo.showName) || formatMessage(commonMessages.operator)}
			</span>
			{text ? (
				<div dangerouslySetInnerHTML={text} />
			) : (
				view.type === ChatDialogEventType.Attach && <ReplyAttachmentPreview id={attachId} />
			)}
		</div>
	);
};

const ReplyMessagePreview = styled(observer(Component))(
	({
		theme: {
			chat: {
				replyMessage: {
					replyPreviewContainer: { senderColor, messageColor },
				},
			},
		},
	}: {
		theme: Theme;
	}) => {
		return {
			'& > span': {
				color: senderColor,
			},
			'& > div': {
				color: messageColor,
			},
		};
	},
	styles
);

export { ReplyMessagePreview };
